import {
  FAIcon,
  isFASlug,
} from "@/block-system/blocks/Table/Block/FontAwesome";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/block-system/brickz/components/ui/tooltip";
import { getZapierBaseUrl } from "@/lib/route-helpers";
import type { APIFieldLatest } from "@/server/services/tables/types/tables-latest";
import { cn } from "@/utils/cn";
import { isIconName } from "@zapier/zinnia-icons";
import { ChevronDown, ChevronUp, ChevronsUpDown, Lock } from "lucide-react";
import Image from "next/legacy/image";
import {
  TABLE_NOT_SORTED_DIRECTION,
  type TableSortDirection,
} from "@/block-system/blocks/Table/constants";
import { Fragment } from "react";
import type { Header } from "@tanstack/react-table";
import type { APIRecord } from "@/server/services/tables/types/records";

export function TableHeaderCell({
  fieldIcon,
  isFieldLocked,
  canSortField,
  fieldName,
  sortDirection,
  onSort,
  tableHeaderElementId,
}: {
  fieldIcon: NonNullable<APIFieldLatest["options"]>["icon"] | undefined;
  isFieldLocked: NonNullable<APIFieldLatest["config"]>["locked"] | undefined;
  fieldName: APIFieldLatest["name"];
  sortDirection: TableSortDirection;
  canSortField: boolean;
  onSort: ((event: unknown) => void) | undefined;
  tableHeaderElementId: string;
}) {
  const lockedIndicator = isFieldLocked ? <FieldLockedIndicator /> : null;
  const icon = isValidFieldIcon(fieldIcon) ? (
    <FieldIcon fieldIcon={fieldIcon} />
  ) : null;

  const hasIcons = lockedIndicator != null || icon != null;

  if (!canSortField) {
    return (
      <div className="flex h-full w-full flex-row items-center gap-2 overflow-hidden whitespace-nowrap">
        {hasIcons ? (
          <div className="flex h-full flex-row items-center gap-1">
            {lockedIndicator}
            {icon}
          </div>
        ) : null}

        <span
          id={tableHeaderElementId}
          className={cn(
            "text-sm font-normal leading-tight text-card-foreground-subtle dark:text-card-foreground-subtle",
            "max-w-full overflow-hidden text-ellipsis"
          )}
        >
          {fieldName}
        </span>
      </div>
    );
  }

  return (
    <Fragment>
      {/* So that we have a different name for the column and the button that sorts the column */}
      <span id={tableHeaderElementId} className="sr-only">
        {fieldName}
      </span>
      <button
        type="button"
        onClick={onSort}
        className="flex h-full w-full flex-row items-center gap-2 overflow-hidden whitespace-nowrap"
        aria-label={getSortIndicatorLabel({ sortDirection, fieldName })}
      >
        {hasIcons ? (
          <div className="flex h-full flex-row items-center gap-1">
            {lockedIndicator}
            {icon}
          </div>
        ) : null}

        <span
          className={cn(
            "text-sm font-normal leading-tight text-card-foreground-subtle dark:text-card-foreground-subtle",
            "max-w-full overflow-hidden text-ellipsis"
          )}
        >
          {fieldName}
        </span>

        <div className="ml-auto">
          <SortIndicator sortDirection={sortDirection} />
        </div>
      </button>
    </Fragment>
  );
}

function getSortIndicatorLabel({
  sortDirection,
  fieldName,
}: {
  sortDirection: TableSortDirection;
  fieldName: string;
}) {
  if (sortDirection === TABLE_NOT_SORTED_DIRECTION) {
    return `Sort ${fieldName}`;
  }

  const readableSortDirection =
    sortDirection === "asc" ? "ascending" : "descending";

  return `Sort ${fieldName} ${readableSortDirection}`;
}

function SortIndicator({
  sortDirection,
}: {
  sortDirection: TableSortDirection;
}) {
  return (
    <div className="flex items-center text-card-foreground dark:text-card-foreground-subtle">
      {sortDirection === "asc" ? (
        <ChevronDown
          data-testid={`sort-indicator-${sortDirection}`}
          color="currentColor"
          size={16}
        />
      ) : null}
      {sortDirection === "desc" ? (
        <ChevronUp
          data-testid={`sort-indicator-${sortDirection}`}
          color="currentColor"
          size={16}
        />
      ) : null}
      {sortDirection === TABLE_NOT_SORTED_DIRECTION ? (
        <ChevronsUpDown
          data-testid="sort-indicator-unsorted"
          color="currentColor"
          size={16}
        />
      ) : null}
    </div>
  );
}

function FieldLockedIndicator() {
  /**
   * This is an arbitrary value.
   */
  const tooltipDelay = 100;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={tooltipDelay}>
        <TooltipTrigger asChild={true}>
          <div
            data-testid="field-locked-indicator"
            className="-mx-1 grid h-full place-content-center px-1 hover:cursor-not-allowed"
            onClick={(event) => {
              /**
               * So that we do not trigger sorting when clicking on the lock icon.
               */
              event.stopPropagation();
              event.preventDefault();
            }}
          >
            <Lock size={16} color="currentColor" aria-hidden={true} />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <span className="font-normal">This field is locked</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function isValidFieldIcon(iconSlug: string | undefined): iconSlug is string {
  if (!iconSlug) {
    return false;
  }

  if (isIconName(iconSlug)) {
    return false;
  }

  return true;
}

function FieldIcon({ fieldIcon }: { fieldIcon: string }) {
  if (isFASlug(fieldIcon)) {
    return <FAIcon slug={fieldIcon} />;
  }

  return (
    <Image
      alt="icon"
      height={16}
      src={`${getZapierBaseUrl()}/generated/${fieldIcon}/32`}
      width={16}
    />
  );
}

export function getTableHeaderElementId({
  header,
  tableId,
}: {
  header: Header<APIRecord, unknown>;
  tableId: string;
}) {
  /**
   * We append the `tableId` to prevent any collisions with other headers in other tables.
   */
  return `table-header-${header.id}-${header.column.id}-${header.index}-${tableId}`;
}
